import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Suspense,
} from "react";
import { Menu, MenuItem, Badge } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import logo from "../assets/logo.webp";
import CustomUserButton from "./UserButton";
import { setUnreadMessagesCount } from "../redux/actions/messageActions";
import { styled } from "@mui/material/styles";
import useMenuOpacity from "../hooks/useMenuOpacity";
const MenuIcon = React.lazy(() => import("@mui/icons-material/Menu"));
const HomeIcon = React.lazy(() => import("@mui/icons-material/Home"));
const NotificationsIcon = React.lazy(
  () => import("@mui/icons-material/Notifications"),
);
const AccountCircleIcon = React.lazy(
  () => import("@mui/icons-material/AccountCircle"),
);
const HistoryIcon = React.lazy(() => import("@mui/icons-material/History"));
const MessageIcon = React.lazy(() => import("@mui/icons-material/Message"));
const SettingsIcon = React.lazy(() => import("@mui/icons-material/Settings"));
const DiamondIcon = React.lazy(() => import("@mui/icons-material/Diamond"));
const BarChartIcon = React.lazy(() => import("@mui/icons-material/BarChart"));

const StyledHamburgerMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    background: "rgba(255, 255, 255, 0.85)",
    borderRadius: "24px",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    backdropFilter: "blur(14px)",
    margin: "0 auto",
    overflow: "hidden",
    width: "180px",
    maxWidth: "90%",
    color: "#1d1d1f",
  },
  zIndex: "5000 !important",
}));

const FooterMenuButton = ({ to, icon, isActive, badgeContent }) => (
  <Link
    to={to}
    onClick={() => window.navigator.vibrate?.(8)}
    className="relative flex items-center justify-center w-[54px] h-[54px] no-underline outline-none"
    style={{ WebkitTapHighlightColor: "transparent" }}
  >
    {isActive && <div className="absolute inset-2 rounded-full bg-black/8" />}
    <div
      className="relative z-10 transition-colors duration-200"
      style={{
        color: isActive ? "rgba(0,0,0,0.9)" : "rgba(0,0,0,0.45)",
      }}
    >
      {React.cloneElement(icon, {
        style: { fontSize: "1.5rem", color: "inherit" },
      })}
    </div>
    {badgeContent > 0 && (
      <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-[#FF3B30] text-[9px] font-bold text-white">
        {badgeContent}
      </span>
    )}
  </Link>
);

const StyledMenuItemComponent = ({
  to,
  icon,
  text,
  badgeContent,
  location,
}) => {
  const isActive =
    location === to ||
    (to === "/history" &&
      [
        "/history",
        "/history/requests-sent",
        "/history/requests-received",
      ].includes(location));

  const base =
    "flex items-center flex-grow min-w-0 max-w-full mb-1 p-4 rounded-xl border transition-colors duration-300";

  // Light mode classes
  const activeBg = "bg-[rgba(0,0,0,0.06)]";
  const activeBorder = "border-[rgba(0,0,0,0.08)]";
  const hoverBg = "hover:bg-[rgba(0,0,0,0.04)]";
  const hoverBorder = "hover:border-[rgba(0,0,0,0.06)]";

  const iconColor = isActive ? "#1d1d1f" : "rgba(0, 0, 0, 0.55)";
  const textColor = isActive ? "text-gray-900" : "text-gray-600";

  const coloredIcon = React.cloneElement(icon, {
    style: { color: iconColor, fontSize: 30 },
  });

  return (
    <li
      className={`${base} ${
        isActive ? `${activeBg} ${activeBorder}` : "border-transparent"
      } ${hoverBg} ${hoverBorder}`}
    >
      <Link
        to={to}
        className="flex items-center w-full no-underline text-inherit"
      >
        <div className="relative flex items-center">
          {badgeContent ? (
            <Badge
              variant={badgeContent > 0 ? "standard" : "dot"}
              color="error"
              overlap="circular"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              showZero
              badgeContent={badgeContent > 0 ? badgeContent : undefined}
            >
              {coloredIcon}
            </Badge>
          ) : (
            coloredIcon
          )}
        </div>
        <span className={`ml-2 text-md hidden xl:inline ${textColor}`}>
          {text}
        </span>
      </Link>
    </li>
  );
};

const LeftMenu = ({ isMobile, chatClient }) => {
  const dispatch = useDispatch();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const user = useSelector((state) => state.loggedUser?.user);
  const unreadNotificationsCount = useSelector(
    (state) => state.notifications?.unreadCount ?? 0,
  );
  const unreadMessagesCount = useSelector(
    (state) => state.messages?.unreadMessagesCount ?? 0,
  );

  const location = useLocation();
  const menuOpacity = useMenuOpacity();
  const lastDispatchedCount = useRef(null);

  const dispatchUnreadCount = useCallback(
    (count) => {
      if (lastDispatchedCount.current !== count) {
        lastDispatchedCount.current = count;
        dispatch(setUnreadMessagesCount(count));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const handleKeyboardShow = (e) => setKeyboardHeight(e.detail.height);
    const handleKeyboardHide = () => setKeyboardHeight(0);
    window.addEventListener("keyboardDidShow", handleKeyboardShow);
    window.addEventListener("keyboardDidHide", handleKeyboardHide);
    return () => {
      window.removeEventListener("keyboardDidShow", handleKeyboardShow);
      window.removeEventListener("keyboardDidHide", handleKeyboardHide);
    };
  }, []);

  const handleMenuOpen = (event) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  useEffect(() => {
    if (!chatClient?.user) return;
    const syncCount = async () => {
      // Use getUnreadCount() for accurate count instead of stale user object
      try {
        const unreadData = await chatClient.getUnreadCount();
        const totalUnread =
          unreadData?.total_unread_count ?? chatClient.user.unread_count ?? 0;
        dispatchUnreadCount(totalUnread);
      } catch (err) {
        // Fallback to cached value if API call fails
        const totalUnread = chatClient.user.unread_count ?? 0;
        dispatchUnreadCount(totalUnread);
      }
    };
    syncCount();
    const handleEvent = (event) => {
      if (event.total_unread_count !== undefined) {
        dispatchUnreadCount(event.total_unread_count);
      } else if (event.unread_count !== undefined) {
        // Some events provide unread_count directly
        dispatchUnreadCount(event.unread_count);
      } else if (
        event.type === "message.read" &&
        event.user?.id === chatClient.userID
      ) {
        // After marking messages as read, fetch fresh count
        syncCount();
      } else if (event.type === "notification.mark_read") {
        // After bulk mark read, fetch fresh count
        syncCount();
      }
    };
    chatClient.on("notification.message_new", handleEvent);
    chatClient.on("notification.mark_read", handleEvent);
    chatClient.on("message.read", handleEvent);
    chatClient.on("connection.changed", syncCount);
    // Also listen for when channels are marked read
    chatClient.on("notification.mark_unread", handleEvent);
    return () => {
      chatClient.off("notification.message_new", handleEvent);
      chatClient.off("notification.mark_read", handleEvent);
      chatClient.off("message.read", handleEvent);
      chatClient.off("connection.changed", syncCount);
      chatClient.off("notification.mark_unread", handleEvent);
    };
  }, [chatClient, dispatch]);

  return (
    <>
      {!isMobile && (
        <div
          className="sticky top-0 left-0 transition-left duration-300 ease-in-out w-[12%] min-w-[12%] p-2 xl:w-72 xl:min-w-[280px] xl:p-4 max-h-[calc(100vh-16px)] overflow-y-auto border-r-[0.5px] bg-white"
          style={{ borderRight: "0.5px solid rgba(0, 0, 0, 0.08)" }}
        >
          <div className="flex items-center mb-6 xl:mb-10 justify-center lg:justify-start gap-2 cursor-pointer">
            <img
              src={logo}
              alt="Logo"
              className="h-[50px] w-[50px] sm:h-[42px] sm:w-[42px] object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)] pointer-events-none select-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              style={{ fetchpriority: "high" }}
            />
            <div
              className="hidden xl:inline-block text-[2.4rem] sm:text-[1.9rem] font-[800] leading-none tracking-[-1.5px] sm:tracking-[-1px] select-none"
              style={{
                fontFamily:
                  '"Proxima Nova", "Inter", "Source Sans Pro", sans-serif',
                background: "#1d1d1f",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              thelinkup
            </div>
          </div>

          <ul className="list-none p-0 m-0">
            <Suspense fallback={<div>Loading...</div>}>
              <StyledMenuItemComponent
                to="/home"
                icon={<HomeIcon style={{ fontSize: 30 }} />}
                text="Home"
                location={location.pathname}
              />
              <StyledMenuItemComponent
                to="/notifications"
                icon={<NotificationsIcon style={{ fontSize: 30 }} />}
                text="Notifications"
                badgeContent={unreadNotificationsCount}
                location={location.pathname}
              />
              <StyledMenuItemComponent
                to="/profile/me"
                icon={<AccountCircleIcon style={{ fontSize: 30 }} />}
                text="Profile"
                location={location.pathname}
              />
              <StyledMenuItemComponent
                to="/history"
                icon={<HistoryIcon style={{ fontSize: 30 }} />}
                text="Linkups"
                location={location.pathname}
              />
              <StyledMenuItemComponent
                to="/messages"
                icon={<MessageIcon style={{ fontSize: 30 }} />}
                text="Messages"
                badgeContent={unreadMessagesCount}
                location={location.pathname}
              />
              <StyledMenuItemComponent
                to="/settings"
                icon={<SettingsIcon style={{ fontSize: 30 }} />}
                text="Settings"
                location={location.pathname}
              />
              {/* Go Pro menu item - hidden while paywall is disabled
              <StyledMenuItemComponent
                to="/pricing"
                icon={<DiamondIcon style={{ fontSize: 30 }} />}
                text="Go Pro"
                location={location.pathname}
              />
              */}
              {user?.is_admin && (
                <StyledMenuItemComponent
                  to="/admin"
                  icon={<BarChartIcon style={{ fontSize: 30 }} />}
                  text="Analytics"
                  location={location.pathname}
                />
              )}
            </Suspense>
          </ul>
          <div className="flex items-center mt-2">
            {/* Ghost/text style for Sign Out button in desktop */}
            <CustomUserButton
              chatClient={chatClient}
              style={{
                background: "none",
                border: "none",
                color: "rgba(0,0,0,0.55)",
                fontWeight: 500,
                fontSize: "15px",
                boxShadow: "none",
                padding: "8px 0",
                textAlign: "left",
                width: "100%",
                cursor: "pointer",
                transition: "color 0.2s",
              }}
              className="hover:text-gray-900 focus:text-gray-900"
            />
          </div>
        </div>
      )}
      {isMobile && keyboardHeight === 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 transition-all duration-500 ease-out active:scale-[0.98] select-none"
          style={{ zIndex: 10, width: "max-content", opacity: menuOpacity }}
        >
          <div
            className="relative flex items-center px-1.5 py-1 rounded-full overflow-hidden"
            style={{
              background: "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: "0 15px 40px rgba(0, 0, 0, 0.12)",
            }}
          >
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                padding: "1.2px",
                background:
                  "linear-gradient(135deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.04) 40%, rgba(0,0,0,0.04) 60%, rgba(0,0,0,0.08) 100%)",
                WebkitMask:
                  "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            <div className="relative z-10 flex items-center">
              <FooterMenuButton
                to="/home"
                icon={<HomeIcon />}
                isActive={location.pathname === "/home"}
              />
              <FooterMenuButton
                to="/notifications"
                icon={<NotificationsIcon />}
                isActive={location.pathname === "/notifications"}
                badgeContent={unreadNotificationsCount}
              />
              <FooterMenuButton
                to="/profile/me"
                icon={<AccountCircleIcon />}
                isActive={location.pathname === "/profile/me"}
              />
              <FooterMenuButton
                to="/history"
                icon={<HistoryIcon />}
                isActive={location.pathname.startsWith("/history")}
              />
              <FooterMenuButton
                to="/messages"
                icon={<MessageIcon />}
                isActive={location.pathname === "/messages"}
                badgeContent={unreadMessagesCount}
              />
            </div>
          </div>

          <button
            onClick={(e) => {
              window.navigator.vibrate?.(10);
              handleMenuOpen(e);
            }}
            className="w-[56px] h-[56px] rounded-full flex items-center justify-center relative overflow-hidden active:scale-90"
            style={{
              background: "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: "0 15px 40px rgba(0, 0, 0, 0.12)",
            }}
          >
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                padding: "1.2px",
                background:
                  "linear-gradient(135deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.04) 40%, rgba(0,0,0,0.04) 60%, rgba(0,0,0,0.08) 100%)",
                WebkitMask:
                  "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            <MenuIcon
              style={{ fontSize: "1.8rem", opacity: 0.85, color: "#1d1d1f" }}
            />
          </button>
        </div>
      )}

      <StyledHamburgerMenu
        disablePortal
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MenuItem
          onClick={handleMenuClose}
          style={{ color: "#1d1d1f", backgroundColor: "transparent" }}
        >
          <Link
            to="/settings"
            className="flex items-center w-full no-underline text-inherit"
          >
            <SettingsIcon style={{ marginRight: 6, color: "#1d1d1f" }} />
            <span>Settings</span>
          </Link>
        </MenuItem>
        {/* Go Pro menu item - hidden while paywall is disabled
        <MenuItem
          onClick={handleMenuClose}
          style={{ color: "#1d1d1f", backgroundColor: "transparent" }}
        >
          <Link
            to="/pricing"
            className="flex items-center w-full no-underline text-inherit"
          >
            <DiamondIcon style={{ marginRight: 6, color: "#1d1d1f" }} />
            <span>Go Pro</span>
          </Link>
        </MenuItem>
        */}
        {user?.is_admin && (
          <MenuItem
            onClick={handleMenuClose}
            style={{ color: "#1d1d1f", backgroundColor: "transparent" }}
          >
            <Link
              to="/admin"
              className="flex items-center w-full no-underline text-inherit"
            >
              <BarChartIcon style={{ marginRight: 6, color: "#0071e3" }} />
              <span>Analytics</span>
            </Link>
          </MenuItem>
        )}
        {/* Divider and extra spacing before logout/profile */}
        <div style={{ height: 24 }} />
        <div style={{ width: "80%", margin: "0 auto" }}>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid rgba(0,0,0,0.1)",
              margin: 0,
            }}
          />
        </div>
        <div style={{ height: 12 }} />
        <MenuItem
          onClick={handleMenuClose}
          style={{ backgroundColor: "transparent", marginTop: 0 }}
        >
          <CustomUserButton chatClient={chatClient} />
        </MenuItem>
      </StyledHamburgerMenu>
    </>
  );
};

export default LeftMenu;
